import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Grid,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import HeaderReservationManagement from "../../layouts/HeaderReservationManagement";
import Navbar from "../../layouts/Navbar";
import TablePaginationLayout from "../../layouts/TablePagination";
import RowTherapist from "../../components/RowTherapist";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import useMonthlyTherapistNomination from "../../features/monthlyTherapistNomination";
import { useEffect, useState } from "react";

const MonthlyTherapistNomination = () => {
  const {
    activeSortColumn,
    sortColumnDirection,
    sortedData,
    rowsPerPage,
    total,
    page,
    selectedMonth,
    searchName,
    handleRowsPerPageChange,
    handlePageChange,
    handleSort,
    handlePreviousMonth,
    handleNextMonth,
    handleMonthChange,
    handleSearchName,
    refetch,
    isLoading,
  } = useMonthlyTherapistNomination();

  const [columnTitles, setColumnTitles] = useState([
    "ID",
    "セラピスト",
    "操作",
  ]);

  useEffect(() => {
    if (sortedData && sortedData.length > 0) {
      const firstRow = sortedData[0];
      const newTitles = [
        "ID",
        "セラピスト",
        ...firstRow.nominations.map((nomination) => {
          return nomination.name;
        }),
        "操作",
      ];
      setColumnTitles(newTitles);
    } else {
      setColumnTitles(["ID", "セラピスト", "操作"]);
    }
  }, [sortedData]);

  const handleSaveSuccess = () => {
    console.log("Refetching data after save...");
    refetch();
  };

  return (
    <>
      <HeaderReservationManagement />
      <Navbar />
      <Box sx={{ px: 4 }}>
        <Typography
          component="p"
          sx={{
            fontSize: {
              xs: 20,
              md: 28,
            },
            color: "#fff",
            top: "104px",
            fontFamily: "Roboto",
            fontStyle: "normal",
            fontWeight: "normal",
            lineHeight: "24px",
            letterSpacing: "3px",
            margin: "80px 0px 16px",
            textDecoration: "none",
            width: "100%",
            paddingBottom: "15px",
            borderBottom: "2px solid white",
            textAlign: "left",
          }}
        >
          月別 セラピスト指名料 設定画面
        </Typography>
        <Box sx={{ marginTop: "40px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handlePreviousMonth}
                  startIcon={<ArrowBackIosNewIcon />}
                >
                  前月
                </Button>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  adapterLocale={ja}
                >
                  <DatePicker
                    views={["month", "year"]}
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    sx={{
                      width: "200px",
                      "& .MuiOutlinedInput-root": {
                        padding: 0,
                        "& fieldset": {
                          border: "none",
                          borderBottom: "1px solid rgba(0, 0, 0, 0.23)",
                          borderRadius: 0,
                        },
                        "&:hover fieldset": {
                          border: "none",
                          borderBottom: "1px solid rgba(0, 0, 0, 0.23)",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                          borderBottom: "2px solid #1976d2",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: 0,
                      },
                    }}
                  />
                </LocalizationProvider>
                <Button
                  variant="outlined"
                  onClick={handleNextMonth}
                  endIcon={<ArrowForwardIosIcon />}
                >
                  次月
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ mt: "20px" }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <TextField
                  placeholder="セラピスト検索"
                  value={searchName}
                  onChange={handleSearchName}
                  sx={{
                    width: "300px",
                    "& .MuiOutlinedInput-root": {
                      padding: 0,
                      "& fieldset": {
                        border: "none",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.23)",
                        borderRadius: 0,
                      },
                      "&:hover fieldset": {
                        border: "none",
                        borderBottom: "1px solid rgba(0, 0, 0, 0.23)",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                        borderBottom: "2px solid #1976d2",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: 0,
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Grid>
          </Grid>
          <Paper
            sx={{ marginTop: "16px", width: "100%", position: "relative" }}
          >
            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <TableContainer>
              <Table aria-labelledby="tableTitle" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow>
                    {columnTitles.map((title, index) => {
                      const isSorted = activeSortColumn === index;
                      const sortDirection = isSorted
                        ? sortColumnDirection
                        : "asc";

                      return (
                        <TableCell
                          key={index}
                          align="center"
                          sx={{
                            padding: "8px 16px",
                            color: "#000",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                          }}
                          sortDirection={isSorted ? sortDirection : false}
                        >
                          {title === "ID" ? (
                            <TableSortLabel
                              active={isSorted}
                              direction={sortDirection}
                              onClick={() => handleSort(index)}
                              sx={{ fontSize: "0.875rem" }}
                            >
                              {title}
                            </TableSortLabel>
                          ) : (
                            title
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData && sortedData.length > 0 ? (
                    sortedData.map((row) => (
                      <RowTherapist
                        key={row.id}
                        row={row}
                        onSaveSuccess={handleSaveSuccess}
                        selectedMonth={selectedMonth}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        データがありません
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePaginationLayout
              rowsPerPage={rowsPerPage}
              total={total}
              page={page}
              rowsPerPageOptions={[50, 100, 125, 10000]}
              onPageChange={handlePageChange}
              handleChangeRowsPerPage={handleRowsPerPageChange}
            />
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default MonthlyTherapistNomination;
